import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import { formatBytes } from '../components/charts';
import {
  Badge, CardSkeleton, EmptyState, Field, Icon, PageHeader, Switch,
} from '../components/ui';

/* ── Storage warnings (Operations, superadmins) ───────────────────────────────
   Tells users their workspace is filling up, on the channel they last used. The work is
   the storage-warning job (backend/services/storage_warnings.py), a Fargate task that
   EventBridge starts every 15 minutes; this page holds its settings and its history.
   The measurements it reads are the control-plane's (services/workspace_usage.py).

   Only where STORAGE_WARNINGS_ENABLED is set (Terraform storage_warnings_enabled, DEV for
   now): elsewhere the sidebar entry is grayed out and this page says why.

   Not here, on purpose: the message (Templates, `storage_warning`), each tier's cap (Tiers)
   and a user's own cap (users.storage_quota_mb). The page links to them. */

const GiB_MiB = 1024;

const KIND_LABEL = { first: 'First warning', crossing: 'Re-send at a percent',
                     resend: 'Automatic re-send', test: 'Test' };
const STATUS_TONE = { sent: 'ok', failed: 'danger', dry_run: 'info' };
const STATUS_LABEL = { sent: 'Sent', failed: 'Failed', dry_run: 'Dry run' };

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—');

// The form holds strings as typed; these turn them into the API's numbers.
const toForm = (s) => ({
  ...s,
  default_cap_gb: String(Math.round((s.default_cap_mb / GiB_MiB) * 100) / 100),
  crossings_text: (s.crossings || []).join(', '),
  first_pct: String(s.first_pct),
  resend_every_days: String(s.resend_every_days),
  retry_hours: String(s.retry_hours),
});

function payload(form) {
  const crossings = form.crossings_text.split(/[\s,;]+/).filter(Boolean).map(Number);
  return {
    armed: form.armed, dry_run: form.dry_run,
    default_cap_mb: Math.round(parseFloat(form.default_cap_gb) * GiB_MiB),
    first_pct: Number(form.first_pct), crossings,
    resend_enabled: form.resend_enabled, resend_every_days: Number(form.resend_every_days),
    retry_hours: Number(form.retry_hours),
    quiet_enabled: form.quiet_enabled, quiet_start: form.quiet_start, quiet_end: form.quiet_end,
    quiet_tz: form.quiet_tz.trim(),
  };
}

function Off() {
  return (
    <EmptyState icon="disk" title="Storage warnings are off on this environment">
      They run only where Terraform sets <code>storage_warnings_enabled</code>, which today is
      DEV. Workspaces are still measured here: Platform, Network and storage, and each user&apos;s
      page show their size.
    </EmptyState>
  );
}

function SettingsCard({ initial, intervalS, onSaved }) {
  const [form, setForm] = useState(() => toForm(initial));
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const set = (k) => (v) => setForm(p => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    const body = payload(form);
    if (!(body.default_cap_mb > 0)) { toast.error('The default cap must be more than 0 GB'); return; }
    if (body.crossings.some(Number.isNaN)) { toast.error('Re-send percents must be numbers'); return; }
    if (body.armed && !initial.armed && !body.dry_run) {
      const ok = await confirm({
        title: 'Arm storage warnings?',
        body: <p className="m-0">Users past the first warning will get a message on their channel at the next run, within 15 minutes.</p>,
        confirmLabel: 'Arm and save', tone: 'primary',
      });
      if (!ok) return;
    }
    setSaving(true);
    const res = await callApi('/api/storage-warnings/settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }, { successMsg: 'Storage warning settings saved', errorMsg: 'Could not save the settings' });
    setSaving(false);
    if (res?.settings) { setForm(toForm(res.settings)); onSaved(res.settings); }
  };

  return (
    <div className="card">
      {confirmDialog}
      <div className="card-header">
        <h2 className="card-title">Settings</h2>
        <div className="row gap-2">
          <Badge tone={form.armed ? (form.dry_run ? 'info' : 'ok') : 'neutral'} dot>
            {form.armed ? (form.dry_run ? 'Armed, dry run' : 'Armed') : 'Not armed'}
          </Badge>
        </div>
      </div>
      <div className="stack gap-4">
        <div className="grid-2">
          <Switch checked={form.armed} onChange={set('armed')} label="Armed"
                  description="Off: users are measured and shown, and nobody is messaged." />
          <Switch checked={form.dry_run} onChange={set('dry_run')} label="Dry run"
                  description="The job records who it would warn in the history and sends nothing. Nobody is marked as told, so turning it off sends for real." />
        </div>

        <div className="card-inset">
          <h3 className="overline mb-3">Caps</h3>
          <Field label="Default workspace cap (GB)" htmlFor="sw-default-cap" className="mb-3"
                 hint="The cap of a user whose tier has none and who has no cap of their own.">
            <input id="sw-default-cap" type="number" min="0" step="0.5" className="form-input num"
                   value={form.default_cap_gb} onChange={set('default_cap_gb')} />
          </Field>
          <div className="notice notice-info mb-0" role="note">
            <Icon name="info" />
            <div className="notice-body">
              Each tier&apos;s cap is set on the <Link to="/tiers">Tiers</Link> page and overrides
              this default. A user can also have a cap of their own
              (<code>storage_quota_mb</code> on the user), which overrides their tier&apos;s; it is
              not editable in the console yet. A user&apos;s page shows which one applies.
            </div>
          </div>
        </div>

        <div className="card-inset">
          <h3 className="overline mb-3">When users are told</h3>
          <div className="grid-2">
            <Field label="First warning at (% of the cap)" htmlFor="sw-first" className="mb-0"
                   hint="One message when a user's last measurement passes this.">
              <input id="sw-first" type="number" min="1" max="99" className="form-input num"
                     value={form.first_pct} onChange={set('first_pct')} />
            </Field>
            <Field label="Re-send at (% of the cap)" htmlFor="sw-crossings" className="mb-0"
                   hint="Optional, comma-separated, e.g. 85, 95. One more message the first time each is passed. Above the first warning, below 100.">
              <input id="sw-crossings" type="text" className="form-input num" placeholder="None"
                     value={form.crossings_text} onChange={set('crossings_text')} />
            </Field>
          </div>
          <p className="text-sm text-muted mt-3 mb-0">
            A user who falls back below the first warning is no longer counted as told, so a
            later climb past it tells them again.
          </p>
        </div>

        <div className="card-inset">
          <div className="row between mb-3">
            <h3 className="overline m-0">Automatic re-send</h3>
          </div>
          <div className="grid-2">
            <Switch checked={form.resend_enabled} onChange={set('resend_enabled')} label="Re-send automatically"
                    description="Only to users still past the first warning when the job runs." />
            <Field label="Every (days)" htmlFor="sw-resend" className="mb-0">
              <input id="sw-resend" type="number" min="1" max="365" className="form-input num"
                     disabled={!form.resend_enabled}
                     value={form.resend_every_days} onChange={set('resend_every_days')} />
            </Field>
          </div>
        </div>

        <div className="card-inset">
          <h3 className="overline mb-3">Quiet hours and retries</h3>
          <Switch checked={form.quiet_enabled} onChange={set('quiet_enabled')} label="Quiet hours"
                  description="No message in this window. A warning that falls due waits until it ends." />
          <div className="grid-2 mt-3">
            <Field label="From" htmlFor="sw-quiet-start" className="mb-0">
              <input id="sw-quiet-start" type="time" className="form-input num" disabled={!form.quiet_enabled}
                     value={form.quiet_start} onChange={set('quiet_start')} />
            </Field>
            <Field label="To" htmlFor="sw-quiet-end" className="mb-0">
              <input id="sw-quiet-end" type="time" className="form-input num" disabled={!form.quiet_enabled}
                     value={form.quiet_end} onChange={set('quiet_end')} />
            </Field>
            <Field label="Time zone" htmlFor="sw-quiet-tz" className="mb-0" hint="An IANA name, e.g. Europe/Rome.">
              <input id="sw-quiet-tz" type="text" className="form-input" disabled={!form.quiet_enabled}
                     value={form.quiet_tz} onChange={set('quiet_tz')} />
            </Field>
            <Field label="Retry a failed delivery after (hours)" htmlFor="sw-retry" className="mb-0">
              <input id="sw-retry" type="number" min="1" max="168" className="form-input num"
                     value={form.retry_hours} onChange={set('retry_hours')} />
            </Field>
          </div>
        </div>

        <div className="notice notice-info mb-0" role="note">
          <Icon name="templates" />
          <div className="notice-body">
            The message is the <strong>Workspace almost full</strong> template, edited on
            the <Link to="/templates">Templates</Link> page. It can use <code>{'{{first_name}}'}</code>,{' '}
            <code>{'{{used}}'}</code>, <code>{'{{cap}}'}</code> and <code>{'{{percent}}'}</code>, and goes out
            with the service tag in front, like every platform message.
          </div>
        </div>

        <div className="row between gap-3">
          <span className="text-sm text-muted">
            {`The job runs every ${Math.round(intervalS / 60)} minutes (set in Terraform).`}
            {initial.updated_by && ` Last changed by ${initial.updated_by}, ${when(initial.updated_at)}.`}
          </span>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TestSend() {
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    if (!/^\d+$/.test(userId.trim())) { toast.error('Enter a user ID'); return; }
    setBusy(true);
    const res = await callApi('/api/storage-warnings/test', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: Number(userId) }),
    }, { errorMsg: 'Could not send the test' });
    setBusy(false);
    if (res) {
      setResult(res);
      if (res.ok) toast.success('Test message sent');
      else toast.error(`Not delivered: ${res.error}`);
    }
  };
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">
          Send a test
          <InfoTip label="About the test send" width={300}>
            Sends the message to one user now, with their last measurement, whatever the
            settings say: armed or not, dry run, quiet hours. It is recorded in the history and
            does not count the user as told.
          </InfoTip>
        </h2>
      </div>
      <div className="row-wrap">
        <input type="text" inputMode="numeric" className="form-input num" style={{ maxWidth: 180 }}
               placeholder="User ID" aria-label="User ID" value={userId}
               onChange={e => setUserId(e.target.value)} />
        <button type="button" className="btn btn-secondary" onClick={send} disabled={busy}>
          <Icon name="send" /> {busy ? 'Sending…' : 'Send test'}
        </button>
      </div>
      {result && (
        <pre className="code-block mt-3 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{result.message}</pre>
      )}
    </div>
  );
}

function TierCaps() {
  const [tiers, setTiers] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const load = useCallback(async () => {
    const r = await apiFetch('/api/storage-warnings/tier-caps');
    const body = r.ok ? await r.json() : null;
    setTiers(body?.tiers || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const missing = (tiers || []).filter(t => t.storage_quota_mb == null);
  const apply = async () => {
    const ok = await confirm({
      title: 'Give these tiers their suggested cap?',
      body: <p className="m-0">{`${missing.map(t => `${t.name}: ${formatBytes(t.suggested_mb * 1024 * 1024, 0)}`).join(', ')}. Caps already set stay as they are.`}</p>,
      confirmLabel: 'Apply', tone: 'primary',
    });
    if (!ok) return;
    const res = await callApi('/api/storage-warnings/tier-caps/apply', { method: 'POST' },
      { successMsg: 'Tier caps applied', errorMsg: 'Could not apply the tier caps' });
    if (res?.tiers) setTiers(res.tiers);
  };
  return (
    <div className="card">
      {confirmDialog}
      <div className="card-header">
        <h2 className="card-title">
          Tier caps
          <InfoTip label="About the suggested caps" width={340}>
            The suggestion ranks tiers by their main monthly limit, an unlimited tier first:
            20 GB for the top, 5 GB less for each tier below, never under 5 GB. Tiers with the
            same limit get the same cap. Edit any cap on the Tiers page.
          </InfoTip>
        </h2>
        <Link to="/tiers" className="btn btn-secondary btn-sm">Open Tiers</Link>
      </div>
      {!tiers ? <CardSkeleton /> : tiers.length === 0 ? <p className="text-muted m-0">No tiers.</p> : (
        <>
          <div className="table-container">
            <table className="table table-compact">
              <thead>
                <tr><th>Tier</th><th className="text-right">Main limit</th>
                  <th className="text-right">Cap</th><th className="text-right">Suggested</th></tr>
              </thead>
              <tbody>
                {tiers.map(t => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td className="text-right num">{t.monthly_cost_limit != null ? `€${t.monthly_cost_limit.toFixed(2)}` : 'Unlimited'}</td>
                    <td className="text-right num">
                      {t.storage_quota_mb != null ? formatBytes(t.storage_quota_mb * 1024 * 1024, 0)
                        : <span className="text-faint">Default</span>}
                    </td>
                    <td className="text-right num text-muted">{formatBytes(t.suggested_mb * 1024 * 1024, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row between gap-2 mt-3">
            <span className="text-sm text-muted">
              {missing.length ? `${missing.length} tier(s) without a cap use the default.` : 'Every tier has a cap.'}
            </span>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!missing.length} onClick={apply}>
              Apply the suggestion to tiers without a cap
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function History() {
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(null);
  const load = useCallback(async () => {
    const r = await apiFetch(`/api/storage-warnings/history?offset=${offset}&limit=15`);
    setPage(r.ok ? await r.json() : { events: [], total: 0 });
  }, [offset]);
  useEffect(() => { load(); }, [load]);
  const total = page?.total || 0;
  const last = Math.min(offset + (page?.events?.length || 0), total);
  return (
    <div className="card card-flush">
      <div className="card-header">
        <h2 className="card-title">History</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load}><Icon name="refresh" /> Refresh</button>
      </div>
      {!page ? <CardSkeleton /> : total === 0 ? (
        <p className="text-muted px-4 py-3 m-0">Nothing sent yet.</p>
      ) : (
        <>
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr><th>When</th><th>User</th><th>Why</th><th className="text-right">Used</th>
                  <th className="text-right">Cap</th><th>Result</th></tr>
              </thead>
              <tbody>
                {page.events.map(e => (
                  <tr key={e.id}>
                    <td data-label="When" className="text-sm">{when(e.at)}</td>
                    <td data-label="User">
                      {e.username ? <Link to={`/users/${e.user_id}`}>{e.username}</Link> : `#${e.user_id}`}
                    </td>
                    <td data-label="Why">
                      {KIND_LABEL[e.kind] || e.kind}
                      {e.level_pct != null && <span className="text-muted">{` · ${e.level_pct}%`}</span>}
                      {e.sent_by && <span className="cell-sub">{`by ${e.sent_by}`}</span>}
                    </td>
                    <td data-label="Used" className="text-right num">{e.bytes_used != null ? formatBytes(e.bytes_used) : '—'}</td>
                    <td data-label="Cap" className="text-right num">{e.cap_bytes != null ? formatBytes(e.cap_bytes) : '—'}</td>
                    <td data-label="Result">
                      <Badge tone={STATUS_TONE[e.status] || 'neutral'}>{STATUS_LABEL[e.status] || e.status}</Badge>
                      {e.error && <span className="cell-sub truncate" title={e.error}>{e.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row between gap-2 px-4 py-3">
            <span className="text-sm text-muted num">{`${offset + 1}–${last} of ${total}`}</span>
            <div className="row gap-2">
              <button type="button" className="btn btn-secondary btn-sm" disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - 15))}>Previous</button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={last >= total}
                      onClick={() => setOffset(offset + 15)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function StorageWarnings() {
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    apiFetch('/api/storage-warnings/settings')
      .then(r => (r.ok ? r.json() : { enabled: false, failed: true }))
      .then(body => { if (live) setState(body); })
      .catch(() => { if (live) setState({ enabled: false, failed: true }); });
    return () => { live = false; };
  }, []);

  return (
    <div>
      <PageHeader description="Tell users their workspace is filling up, on the channel they last used.">
        <h1 className="page-title">Storage warnings</h1>
      </PageHeader>
      {!state ? <CardSkeleton /> : state.failed ? (
        <div className="notice notice-danger" role="alert"><div className="notice-body">Could not load the storage warning settings.</div></div>
      ) : !state.enabled ? <Off /> : (
        <div className="stack gap-4">
          <SettingsCard initial={state.settings} intervalS={state.job_interval_s}
                        onSaved={s => setState(p => ({ ...p, settings: s }))} />
          <div className="grid-2">
            <TestSend />
            <TierCaps />
          </div>
          <History />
        </div>
      )}
    </div>
  );
}
